import { Section, SelectOption, useNotify } from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  LeadConsumptionQuantityByProfileRequestModel,
  LeadModel
} from '../../../../../api/Argentia/endpoints/leads/model/leads'
import Page from '../../../../../components/page/page'
import PageHeader from '../../../../../components/page/pageHeader'
import { QuantityAssistantDataTableRow } from '../../../../../components/shared/quantityAssistantModal/quantityAssistantModal.model'
import { getUserPermissions } from '../../../../../helpers/permissionsHelper'
import { missingFieldsFormWithRequiredText } from '../../../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../../../hooks'
import { InformationDialogs } from '../../../../../model/enums/informationDialogs'
import { Pages } from '../../../../../model/RBAC/constants'
import { measureUnitToSelectOptions } from '../../../../flexibility/flexibility.mapper'
import { fetchMeasureUnitPowerType } from '../../../../flexibility/flexibility.service'
import { profileModelToSelectOptions } from '../../../../profiles/profiles.mapper'
import { fetchProfilesByLeadType } from '../../../../profiles/profiles.services'
import { mapQuantityDataTableToRequest } from '../../../leads.mapper'
import { createConsumptionQuantityByProfile } from '../../../leads.service'
import { ProfileQuantityCard, ProfileQuantityFormModel } from './components/profileQuantityCard'

interface LeadsConsumptionProfilePageProps {
  location?: {
    state: {
      leadData?: LeadModel
    }
  }
}

export const LeadsConsumptionProfilePage: React.FC<LeadsConsumptionProfilePageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'leads'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getInformation, closeConfirmation } = useConfirmationDialog()

  const [measureOptions, setMeasureOptions] = useState<SelectOption[]>([])
  const [profilesOptions, setProfilesOptions] = useState<SelectOption[]>([])

  const [initialTableData, setInitialTableData] = useState<QuantityAssistantDataTableRow[]>([])
  const [tableData, setTableData] = useState<QuantityAssistantDataTableRow[]>([])
  const [formData, setFormData] = useState<ProfileQuantityFormModel>({} as ProfileQuantityFormModel)
  const [formDataSections, setFormDataSections] = useState<Section[]>([])

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isFetchingMeasureOptions, setIsFetchingMeasureOptions] = useState<boolean>(false)
  const [isFetchingProfilesOptions, setIsFetchingProfilesOptions] = useState<boolean>(false)

  const leadData = props.location?.state?.leadData
  const commodity = leadData?.commodity ?? 1
  const breakdownLevel = leadData?.breakdownLevel ?? leadData?.product?.breakdownLevel
  const weightedType = leadData?.weightedType ?? leadData?.product?.weightedType
  const hasSupplyPoint = leadData?.supplyPoint?.id

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  const handleSaveButtonClick = () => {
    if (missingFieldsFormWithRequiredText(formDataSections, formData)) {
      error(t('leads:notifications.incompleteForm'))
      return
    }
    const request = mapQuantityDataTableToRequest(tableData, leadData, formData)
    saveQuantity(request)
  }

  // ------------------- Profile Quantity Card Handlers -----------------------------//

  const handleDataTableChange = (newTableData: QuantityAssistantDataTableRow[]) => {
    setTableData(newTableData)
  }

  const handleFormDataChange = (formData: ProfileQuantityFormModel, formSections: Section[]) => {
    setFormDataSections(formSections)
    setFormData(formData)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const displaySupplyPointErrorMessage = async () => {
    await getInformation(InformationDialogs.MISSING_SUPPLY_POINT).then(() => {
      history.goBack()
      closeConfirmation()
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const saveQuantity = async (request: LeadConsumptionQuantityByProfileRequestModel) => {
    setIsFetching(true)
    await createConsumptionQuantityByProfile(request)
      .then(response => {
        if (response.success) {
          info(t('leads:notifications.saveConsumptionProfile.success'))
          history.goBack()
        } else {
          error(t('leads:notifications.saveConsumptionProfile.error'))
        }
      })
      .finally(() => setIsFetching(false))
  }

  useEffect(() => {
    const fetchMeasureOptions = async () => {
      setIsFetchingMeasureOptions(true)
      await fetchMeasureUnitPowerType()
        .then(response => {
          if (response.success) {
            setMeasureOptions(measureUnitToSelectOptions(response.data ?? []))
          }
        })
        .finally(() => setIsFetchingMeasureOptions(false))
    }
    fetchMeasureOptions()
  }, [])

  useEffect(() => {
    const fetchProfilesOptionsByLeadType = async () => {
      if (commodity && breakdownLevel) {
        setIsFetchingProfilesOptions(true)
        await fetchProfilesByLeadType(commodity, breakdownLevel)
          .then(response => {
            if (response.success) {
              setProfilesOptions(profileModelToSelectOptions(response.data ?? []))
            }
          })
          .finally(() => setIsFetchingProfilesOptions(false))
      }
    }

    fetchProfilesOptionsByLeadType()
  }, [commodity, breakdownLevel])

  // #endregion Hooks Ends

  if (!hasSupplyPoint) {
    displaySupplyPointErrorMessage()
  }

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('leads:components.leadsConsumptionProfile.header.title')}>
        <Button onClick={handleBackButtonClick} variant="text" color="primary" size="small">
          {t('leads:components.leadsConsumptionProfile.header.backButton')}
        </Button>
        <Button onClick={handleSaveButtonClick} variant="contained" color="primary" size="small">
          {t('leads:components.leadsConsumptionProfile.header.saveButton')}
        </Button>
      </PageHeader>

      <ProfileQuantityCard
        breakdownLevelId={breakdownLevel}
        weightedTypeId={weightedType}
        profileId={leadData?.profile?.id}
        commodity={commodity}
        data={initialTableData}
        isLoading={isFetching}
        userPermissions={getUserPermissions(Pages.Leads)}
        onChangeDataTable={handleDataTableChange}
        onFormDataChange={handleFormDataChange}
      />
    </Page>
  )
}
